// @ts-nocheck
import React from 'react';
import InteractiveSlider from '../InteractiveSlider';
import { CarComparisonData, LeaseInputMode } from '../../types/carComparison';
import { renderInputSection } from '../../utils/renderInputSection';
import { calculateLeaseAPR, calculateLeasePayment, calculateLeaseResidualValue } from '../../utils/carCalculations';

interface LeaseInputsProps {
  data: CarComparisonData;
  onInputChange: (field: keyof CarComparisonData, value: any) => void;
  showGuidance: (title: string, content: string) => void;
  formatCurrency: (value: number) => string;
  leaseInputMode: LeaseInputMode;
  setLeaseInputMode: (mode: LeaseInputMode) => void;
  includeLeaseScenario: boolean;
  setIncludeLeaseScenario: (include: boolean) => void;
}

const LeaseInputs: React.FC<LeaseInputsProps> = ({
  data,
  onInputChange,
  showGuidance,
  formatCurrency,
  leaseInputMode,
  setLeaseInputMode,
  includeLeaseScenario,
  setIncludeLeaseScenario
}) => {

  return (
    renderInputSection('Lease Scenario', (
      <>
        {/* Toggle to enable/disable lease scenario */}
        <div className="interactive-slider">
          <div className="label-with-guidance" style={{"display":"block","textAlign":"center"}}>
              <input
                type="checkbox"
                checked={includeLeaseScenario}
                onChange={(e) => setIncludeLeaseScenario(e.target.checked)}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <br/>
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                Compare Leasing?
              </span>
              <br/>
            <small style={{ display: 'block', marginLeft: '28px', color: '#666', marginTop: '0.25rem' }}>
              Consider leasing? Include this if you know want to know how much this costs!
            </small>
          </div>
        </div>

        {includeLeaseScenario && (
        <>
          <InteractiveSlider
            config={{
              min: 1,
              max: 5,
              step: 1,
              defaultValue: 3,
              format: 'number',
              label: 'Lease Term (years)'
            }}
            value={data.leaseTerm}
            onChange={(value) => onInputChange('leaseTerm', value)}
            guidanceTitle="Lease Term"
            guidanceContent={
              "How long is your lease agreement?<br><br>" +
              '<strong>Common lease terms:</strong><br>' +
              '• 2 years: Short-term, higher monthly payments<br>' +
              '• 3 years: Most popular - good balance<br>' +
              '• 4-5 years: Lower payments, but longer commitment<br><br>' +
              '<strong>Consider your lifestyle:</strong><br>' +
              '• Planning to move soon? Shorter term<br>' +
              '• Want the latest tech? 2-3 years<br>' +
              '• Budget conscious? 4-5 years<br><br>' +
              'Most people choose 3 years - it\'s the sweet spot!'
            }
            onShowGuidance={showGuidance}
          />
        

          <InteractiveSlider
            config={{
              min: 0,
              max: Math.max(20000, data.totalCost),
              step: 500,
              defaultValue: 0,
              format: 'number',
              label: 'Lease Down Payment'
            }}
            value={data.leaseDownPayment}
            onChange={(value) => onInputChange('leaseDownPayment', value)}
            guidanceTitle="Lease Down Payment Risks"
            guidanceContent={
              "Lease down payments (cap cost reduction) can be risky in certain situations.<br><br>" +
              '<strong>Key Risks:</strong><br>' +
              '• <strong>Accident Risk:</strong> If your leased car is totaled in an accident, you lose your down payment<br>' +
              '• <strong>Gap Insurance:</strong> Standard insurance may not cover the full lease amount<br>' +
              '• <strong>Early Termination:</strong> If you end the lease early, you may not recover your down payment<br>' +
              '• <strong>No Ownership:</strong> You don\'t own the car, so the down payment doesn\'t build equity<br><br>' +
              '<strong>When Down Payments Make Sense:</strong><br>' +
              '• You have excellent driving record and low accident risk<br>' +
              '• You plan to keep the car for the full lease term<br>' +
              '• The down payment significantly reduces monthly payments<br>' +
              '• You have gap insurance coverage<br><br>' +
              '<strong>Alternatives:</strong><br>' +
              '• Consider $0 down payment leases<br>' +
              '• Use the money for investments instead<br>' +
              '• Keep cash for emergency fund<br><br>' +
              '<strong>Protection Tips:</strong><br>' +
              '• Purchase gap insurance to protect your down payment<br>' +
              '• Consider lease protection products<br>' +
              '• Review your auto insurance coverage limits'
            }
            onShowGuidance={showGuidance}
          />

        
        <InteractiveSlider
          config={{
            min: 0,
            max: 60000,
            step: 1000,
            defaultValue: 36000,
            format: 'number',
            label: 'Total Mileage Allowance'
          }}
          value={data.totalMileageAllowance}
          onChange={(value) => onInputChange('totalMileageAllowance', value)}
        />
        
        <InteractiveSlider
          config={{
            min: 0.05,
            max: 0.50,
            step: 0.01,
            defaultValue: 0.15,
            format: 'currency',
            label: 'Cost per Extra Mile'
          }}
          value={data.costPerExtraMile}
          onChange={(value) => onInputChange('costPerExtraMile', value)}
        />
        
        {/* Lease Calculation Mode Selector */}
        <div className="interactive-slider">
          <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
            <div style={{fontWeight: "bold"}}>Lease Calculation Mode</div>
          </div>
              <button
                type="button"
                className={`lease-mode-btn ${leaseInputMode === 'payment-apr' ? 'active' : ''}`}
                onClick={() => setLeaseInputMode('payment-apr')}
              >
                Payment + APR → Residual
              </button>
              <button
                type="button"
                className={`lease-mode-btn ${leaseInputMode === 'payment-residual' ? 'active' : ''}`}
                onClick={() => setLeaseInputMode('payment-residual')}
              >
                Payment + Residual → APR
              </button>
              <button
                type="button"
                className={`lease-mode-btn ${leaseInputMode === 'apr-residual' ? 'active' : ''}`}
                onClick={() => setLeaseInputMode('apr-residual')}
              >
                APR + Residual → Payment
              </button>
            </div>


        {/* Dynamic Lease Inputs */}
        {leaseInputMode === 'payment-apr' && (
          <>
            <InteractiveSlider
              config={{
                min: 0,
                max: 20000,
                step: 100,
                defaultValue: 400,
                format: 'currency',
                label: 'Monthly Lease Payment'
              }}
              value={data.leasePayment}
              onChange={(value) => onInputChange('leasePayment', value)}
            />
            
            <InteractiveSlider
              config={{
                min: 0,
                max: 20,
                step: 0.1,
                defaultValue: 5,
                format: 'percent',
                label: 'Lease APR (%)'
              }}
              value={data.leaseAPR}
              onChange={(value) => onInputChange('leaseAPR', value)}
            />
            <div className="calculated-value">
              <strong>Calculated Residual: {formatCurrency(calculateLeaseResidualValue(data))}</strong>
            </div>
          </>
        )}

        {leaseInputMode === 'payment-residual' && (
          <>
            <InteractiveSlider
              config={{
                min: 0,
                max: 20000,
                step: 100,
                defaultValue: 400,
                format: 'currency',
                label: 'Monthly Lease Payment'
              }}
              value={data.leasePayment}
              onChange={(value) => onInputChange('leasePayment', value)}
            />
            
            <InteractiveSlider
              config={{
                min: 0,
                max: 500000,
                step: 1000,
                defaultValue: 0,
                format: 'number',
                label: 'Residual Value'
              }}
              value={data.residualValue}
              onChange={(value) => onInputChange('residualValue', value)}
            />
          {/* Calculated Monthly Payment Display */}
            <div className="calculated-value">
              <strong>Calculated APR: {calculateLeaseAPR(data)}</strong>
            </div>
          </>
        )}

        {leaseInputMode === 'apr-residual' && (
          <>
            <InteractiveSlider
              config={{
                min: 0,
                max: 20,
                step: 0.1,
                defaultValue: 5,
                format: 'percent',
                label: 'Lease APR (%)'
              }}
              value={data.leaseAPR}
              onChange={(value) => onInputChange('leaseAPR', value)}
            />

            <InteractiveSlider
              config={{
                min: 0,
                max: 500000,
                step: 1000,
                defaultValue: 0,
                format: 'number',
                label: 'Residual Value'
              }}
              value={data.residualValue}
              onChange={(value) => onInputChange('residualValue', value)}
            />

            {/* Calculated Monthly Payment Display */}
            <div className="calculated-value">
              <strong>Calculated Monthly Payment: {formatCurrency(calculateLeasePayment(data))}</strong>
            </div>
          </>
        )}
        
        <InteractiveSlider
          config={{
            min: 0,
            max: 5000,
            step: 50,
            defaultValue: 0,
            format: 'currency',
            label: 'Lease Origination Fees'
          }}
          value={data.leaseOriginationFees}
          onChange={(value) => onInputChange('leaseOriginationFees', value)}
          guidanceTitle="Lease Origination Fees"
          guidanceContent={
            '<strong>⚠️ ONLY applies to the "Lease" scenario - NOT Buy (cash) or Finance</strong><br><br>' +
            "Fees charged to initiate the lease (upfront, not end-of-lease):<br><br>" +
            '<strong>Common origination fees:</strong><br>' +
            '• Acquisition fee ($500-1,000)<br>' +
            '• Documentation fee ($200-500)<br>' +
            '• First month payment (rolled into lease)<br>' +
            '• License/registration fees<br><br>' +
            '<strong>Note:</strong> Tax is applied to these fees. These are separate from end-of-lease fees (disposition, excess wear, etc.).<br><br>' +
            '<strong>Tip:</strong> Some dealers will waive the acquisition fee - always negotiate!'
          }
          onShowGuidance={showGuidance}
        />

        <InteractiveSlider
          config={{
            min: 0,
            max: 50000,
            step: 1000,
            defaultValue: 500,
            format: 'number',
            label: 'Lease Buyout Fees'
          }}
          value={data.leaseFees}
          onChange={(value) => onInputChange('leaseFees', value)}
        />

        {/* End of Lease Decision */}
        <div className="interactive-slider">
          <div className="label-with-guidance" style={{paddingBottom: "0.5rem"}}>
            <div style={{fontWeight: "bold"}}>End of Lease Decision</div>
            <small style={{ display: 'block', color: '#666', marginTop: '0.25rem' }}>
              What happens when the lease term ends?
            </small>
          </div>

          {/* Scenario 1: Ownership < Lease Term - Warning */}
          {data.ownershipTimeframe < data.leaseTerm && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              marginTop: '0.5rem'
            }}>
              <div style={{ fontWeight: 'bold', color: '#856404', marginBottom: '0.5rem' }}>
                ⚠️ Early Return Warning
              </div>
              <div style={{ fontSize: '0.9rem', color: '#856404' }}>
                Your ownership period ({data.ownershipTimeframe} years) is shorter than the lease term ({data.leaseTerm} years).
                <br/><br/>
                Returning a car before the lease ends can be tricky and expensive:
                <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                  <li>Early termination fees apply</li>
                  <li>You may owe remaining payments</li>
                  <li>Disposition fees still charged</li>
                </ul>
              </div>
            </div>
          )}

          {/* Scenario 2: Ownership > Lease Term - Info */}
          {data.ownershipTimeframe > data.leaseTerm && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#d1ecf1',
              border: '1px solid #17a2b8',
              borderRadius: '8px',
              marginTop: '0.5rem'
            }}>
              <div style={{ fontWeight: 'bold', color: '#0c5460', marginBottom: '0.5rem' }}>
                ℹ️ Extended Ownership
              </div>
              <div style={{ fontSize: '0.9rem', color: '#0c5460' }}>
                Your ownership period ({data.ownershipTimeframe} years) is longer than the lease term ({data.leaseTerm} years).
                <br/><br/>
                We've assumed you will <strong>buy out the lease</strong> at the end of the term and keep/sell the car to reach your desired ownership period.
              </div>
            </div>
          )}

          {/* Scenario 3: Ownership == Lease Term - Three Options */}
          {data.ownershipTimeframe === data.leaseTerm && (
            <div style={{ marginTop: '0.5rem' }}>
              <button
                type="button"
                className={`lease-mode-btn ${data.endOfLeaseOption === 'buyout-and-sell' ? 'active' : ''}`}
                onClick={() => onInputChange('endOfLeaseOption', 'buyout-and-sell')}
                style={{ marginBottom: '0.5rem' }}
              >
                Buyout & Sell
              </button>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem', paddingLeft: '0.5rem' }}>
                Purchase the car at residual value, then sell it immediately
              </div>

              <button
                type="button"
                className={`lease-mode-btn ${data.endOfLeaseOption === 'return-lease' ? 'active' : ''}`}
                onClick={() => onInputChange('endOfLeaseOption', 'return-lease')}
                style={{ marginBottom: '0.5rem' }}
              >
                Return Lease
              </button>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem', paddingLeft: '0.5rem' }}>
                Return the car to the dealer (you walk away with nothing)
              </div>

              <button
                type="button"
                className={`lease-mode-btn ${data.endOfLeaseOption === 'choose-best' ? 'active' : ''}`}
                onClick={() => onInputChange('endOfLeaseOption', 'choose-best')}
              >
                Choose Best Option
              </button>
              <div style={{ fontSize: '0.85rem', color: '#666', paddingLeft: '0.5rem' }}>
                We calculate which option saves you the most money
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </>
    ))
  );
};

export default LeaseInputs;